import { Search } from 'lucide-react';
import type { KeyboardEventHandler, ReactNode } from 'react';
import { Skeleton } from '../Skeleton';

/**
 * The width rule for a search group sitting in a **page toolbar**: it takes every pixel Create
 * leaves it.
 *
 * It was capped at half the row, so that on a wide monitor the caret, the record count and the
 * settings cog did not end up a screen apart. Product call, 11 Sep 2026 — the cap is gone: the
 * placeholder is the widest thing in the group and a short one left a stub of a control
 * floating in the middle of an empty row, which read worse than a long strip does.
 *
 * Defined once and imported, not retyped. `<IndexToolbar>` is not the only toolbar in the
 * product — the org chart's Manage page hand-rolls the same row — and a literal copied into the
 * second one is how the two stopped matching the first time.
 *
 * `ml-auto` survives the cap going, and it is not decoration: Create is permission-gated, so on
 * a row where it does not render `justify-between` would have nothing to push against. With
 * `flex-1` the auto margin resolves to nothing and the group simply fills the row, which is the
 * point; without Create it still fills it.
 */
export const TOOLBAR_SEARCH_WIDTH = 'ml-auto flex-1';

/**
 * The standard search input group — one connected control: search, then a bordered record
 * count where there is one, then whatever segments the caller appends.
 *
 * The wrapper owns the border and the focus ring so the segments read as parts of a single
 * field rather than adjacent widgets, and the count is a segment of the input rather than a
 * caption floating beside it.
 *
 * Used by `<IndexToolbar>` on every list page and by any embedded table that offers search —
 * a panel inside a profile card gets the same strip as a full index, so the two never drift
 * into two different-looking search boxes.
 */
export function SearchGroup({
  id,
  value,
  onChange,
  placeholder,
  total,
  loading = false,
  className = '',
  children,
  leading,
  onKeyDown,
  flush = false,
}: {
  /** Prefix for the ids: `${id}-search` and `${id}-record-count`. */
  id: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  /** Opt-in key handling on the field — see the note at the input. */
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  /**
   * The record count, in its own bordered segment.
   *
   * **Omit it and the segment is not drawn.** The filter rail is the caller that does: its
   * rows are a facet's values rather than records, so there is no count that would be true
   * of them — and the segment was taking width the search itself needed, in a strip only as
   * wide as the rail.
   */
  total?: number;
  loading?: boolean;
  /**
   * Width constraints for the group, appended to its own classes.
   *
   * The group grows to fill its row by default, which is right for a search strip over an
   * embedded table. A caller that wants it capped passes a `max-w-*` — a max-width clamps a
   * flex item even while `flex-1` is still growing it, which is why the cap belongs here
   * rather than as a replacement for the growth.
   */
  className?: string;
  /** Further bordered segments after the count — a settings cog. */
  children?: ReactNode;
  /**
   * Bordered segments BEFORE the search field — the filter toggle.
   *
   * Its own slot rather than the head of `children`, because the two ends of the group are
   * different places: what narrows the list sits where the reader starts reading, and what
   * configures the surface sits at the far end. A single slot could only ever put both at one
   * end.
   */
  leading?: ReactNode;
  /**
   * Drops the box and squares the corners, for a group that IS the edge of its surface — the
   * sidebar's search sits flush against the rail (26 Oct 2026).
   *
   * **A prop that SWAPS the frame, never a `className` that appends one.** `rounded-none`
   * beside `rounded-lg` is one property at one specificity, decided by Tailwind's emission
   * order rather than by the call site — the rule `<TextField trailingWidth>` and the inline
   * grid's cells already follow. What survives here is the BOTTOM rule, which is the group's
   * own edge against what sits under it.
   */
  flush?: boolean;
}) {
  return (
    <div
      className={`flex min-w-0 flex-1 items-stretch bg-white transition focus-within:ring-2 focus-within:ring-blue-100 ${
        flush
          ? 'border-b border-slate-300 focus-within:border-blue-500'
          : 'rounded-lg border border-slate-300 focus-within:border-blue-500'
      } ${className}`}
    >
      {leading}

      <div className="relative min-w-0 flex-1">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
          aria-hidden="true"
        />
        <input
          id={`${id}-search`}
          name={`${id}-search`}
          type="search"
          /*
            The browser's own form-history dropdown is REFUSED (26 Oct 2026). A named
            `type="search"` field is remembered across submits, so Chrome drew a list of
            previously typed strings over the live results underneath — two answers to one
            query, and the stale one on top. The app owns what this field suggests.
            `<Combobox>`, `<MultiSelect>` and `<Select>` already refuse it for the same reason.
          */
          autoComplete="off"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          aria-label={placeholder}
          /*
            Opt-in, for a caller whose results are keyboard-navigable — the sidebar search moves
            focus into its list on ArrowDown (26 Oct 2026). Deliberately just a key handler and
            NOT combobox ARIA: the results there are real links that Enter activates natively,
            and `role="option"` would override the link semantics a screen reader needs. A
            caller that passes nothing is untouched.
          */
          onKeyDown={onKeyDown}
          /*
            No left radius when something leads the group: the rounded corner belongs to
            whichever segment is actually first, or the input's curve cuts across the segment
            beside it.
          */
          className={[
            'min-h-10 w-full border-0 bg-transparent py-2 pl-10 pr-3 text-sm text-slate-800',
            'placeholder:text-slate-400 focus:outline-none focus:ring-0',
            leading || flush ? '' : 'rounded-l-lg',
          ].join(' ')}
        />
      </div>

      {/* Bordered segment: part of the field, not a caption floating beside it. */}
      {total === undefined ? null : (
        <span
          id={`${id}-record-count`}
          className="flex shrink-0 items-center border-l border-slate-300 px-3 text-xs text-slate-500"
        >
          {loading ? (
            <Skeleton className="h-3.5 w-16" />
          ) : (
            <>
              <span className="font-mono tabular-nums">{total}</span>
              <span className="ml-1">{total === 1 ? 'record' : 'records'}</span>
            </>
          )}
        </span>
      )}

      {children}
    </div>
  );
}
